<template>
  <div 
    v-if="isOpen" 
    class="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
  >
    <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200">
      <!-- Modal Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white flex justify-between items-center">
        <div class="flex items-center gap-2">
          <i class="fa fa-receipt text-lg" />
          <div>
            <h3 class="font-bold text-base">Registrar Nuevo Gasto</h3>
            <p class="text-[11px] text-orange-100">Egreso directo al negocio (sin afectar inventario)</p>
          </div>
        </div>
        <button 
          type="button" 
          class="text-white/80 hover:text-white transition-colors cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times text-lg" />
        </button>
      </div>

      <!-- Modal Form -->
      <form class="p-6 space-y-4" @submit.prevent="saveQuickSpent">
        <!-- Notice -->
        <div class="p-3 bg-orange-50 border border-orange-200 rounded-xl text-xs text-orange-900 flex items-start gap-2">
          <i class="fa fa-info-circle text-orange-600 mt-0.5" />
          <div>
            <strong>Regla de negocio:</strong> Los gastos constituyen salidas de dinero operativas. No suman productos al stock y el proveedor es opcional.
          </div>
        </div>

        <!-- Concepto -->
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Concepto / Nombre *</label>
          <input 
            v-model="quickSpentForm.name"
            type="text"
            required
            placeholder="Ej: Pago de Luz, Alquiler, Artículos de Limpieza..."
            class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          />
        </div>

        <!-- Categoría & Proveedor (opcional) -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Categoría *</label>
            <select 
              v-model="quickSpentForm.spent_category_id"
              required
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            >
              <option value="">Seleccionar categoría</option>
              <option v-for="cat in spentCategories" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">
              Proveedor (Opcional)
            </label>
            <select 
              v-model="quickSpentForm.provider_id"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            >
              <option :value="null">Sin proveedor / Gasto interno</option>
              <option v-for="prov in providers" :key="prov.id" :value="prov.id">
                {{ prov.name }} {{ prov.company_name && prov.company_name !== prov.name ? '(' + prov.company_name + ')' : '' }}
              </option>
            </select>
          </div>
        </div>

        <!-- Moneda & Monto -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Moneda *</label>
            <div class="flex bg-gray-100 p-1 rounded-lg">
              <button 
                type="button" 
                :class="quickSpentForm.currency === 'USD' ? 'bg-white text-orange-700 shadow-xs font-bold' : 'text-gray-500'"
                class="flex-1 py-1.5 rounded text-xs transition-all cursor-pointer"
                @click="setQuickCurrency('USD')"
              >
                Dólares ($)
              </button>
              <button 
                type="button" 
                :class="quickSpentForm.currency === 'BS' ? 'bg-white text-orange-700 shadow-xs font-bold' : 'text-gray-500'"
                class="flex-1 py-1.5 rounded text-xs transition-all cursor-pointer"
                @click="setQuickCurrency('BS')"
              >
                Bolívares (Bs.)
              </button>
            </div>
          </div>

          <div v-if="quickSpentForm.currency === 'BS'">
            <label class="block text-xs font-bold text-gray-700 mb-1">Monto en Bs. *</label>
            <input 
              v-model.number="quickBsAmount"
              type="number"
              step="0.01"
              min="0.01"
              required
              placeholder="0.00"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              @input="calculateQuickAmountFromBs"
            />
            <p class="text-[10px] text-gray-400 mt-0.5">Tasa: {{ exchangeRate }} Bs/$</p>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ quickSpentForm.currency === 'BS' ? 'Equivalente USD (Auto)' : 'Monto en USD *' }}
            </label>
            <input 
              v-model.number="quickSpentForm.amount"
              type="number"
              step="0.01"
              min="0.01"
              required
              :readonly="quickSpentForm.currency === 'BS'"
              placeholder="0.00"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            />
          </div>
        </div>

        <!-- Fecha & Método de Pago -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Fecha *</label>
            <input 
              v-model="quickSpentForm.date"
              type="date"
              required
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Método de Pago *</label>
            <select 
              v-model="quickSpentForm.method"
              required
              class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia Bancaria</option>
              <option value="pago-movil">Pago Móvil</option>
              <option value="zelle">Zelle</option>
              <option value="punto-de-venta">Punto de Venta</option>
            </select>
          </div>
        </div>

        <!-- Comentario / Notas -->
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Descripción / Notas</label>
          <textarea 
            v-model="quickSpentForm.description"
            rows="2"
            placeholder="Detalles adicionales sobre el gasto..."
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          />
        </div>

        <!-- Modal Footer -->
        <div class="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <Link 
            href="/shoppings/create?type=spent" 
            class="text-xs font-semibold text-gray-500 hover:text-orange-600 transition-colors"
          >
            Abrir formulario completo
          </Link>

          <div class="flex items-center gap-2">
            <button 
              type="button" 
              class="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              @click="$emit('close')"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSavingSpent"
              class="px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <i v-if="isSavingSpent" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-save" />
              <span>Guardar Gasto</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Swal from 'sweetalert2'

export default {
  name: 'ShoppingQuickSpentModal',

  components: {
    Link,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    providers: {
      type: Array,
      default: () => [],
    },
    spentCategories: {
      type: Array,
      default: () => [],
    },
    currentExchangeRate: {
      type: [Number, String],
      default: 0,
    },
  },

  emits: ['close'],

  data() {
    return {
      isSavingSpent: false,
      quickBsAmount: null,
      quickSpentForm: {
        name: '',
        spent_category_id: this.spentCategories.length ? this.spentCategories[0].id : '',
        provider_id: null,
        currency: 'USD',
        amount: null,
        exchange_rate: parseFloat(this.currentExchangeRate) || 0,
        date: new Date().toISOString().slice(0, 10),
        method: 'efectivo',
        description: '',
      },
    }
  },

  computed: {
    exchangeRate() {
      return parseFloat(this.currentExchangeRate) || 0
    },
  },

  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.resetForm()
      }
    },
  },

  methods: {
    resetForm() {
      this.quickBsAmount = null
      this.quickSpentForm = {
        name: '',
        spent_category_id: this.spentCategories.length ? this.spentCategories[0].id : '',
        provider_id: null,
        currency: 'USD',
        amount: null,
        exchange_rate: this.exchangeRate,
        date: new Date().toISOString().slice(0, 10),
        method: 'efectivo',
        description: '',
      }
    },

    setQuickCurrency(curr) {
      this.quickSpentForm.currency = curr
      if (curr === 'USD') {
        this.quickBsAmount = null
      } else {
        this.quickSpentForm.exchange_rate = this.exchangeRate
        this.calculateQuickAmountFromBs()
      }
    },

    calculateQuickAmountFromBs() {
      const rate = parseFloat(this.quickSpentForm.exchange_rate) || this.exchangeRate
      if (this.quickBsAmount && rate > 0) {
        this.quickSpentForm.amount = parseFloat((this.quickBsAmount / rate).toFixed(2))
      } else {
        this.quickSpentForm.amount = 0
      }
    },

    saveQuickSpent() {
      if (!this.quickSpentForm.name) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe ingresar el concepto del gasto' })
        return
      }
      if (!this.quickSpentForm.amount || this.quickSpentForm.amount <= 0) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'El monto del gasto debe ser mayor a 0' })
        return
      }
      if (!this.quickSpentForm.spent_category_id) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Debe seleccionar una categoría de gasto' })
        return
      }

      this.isSavingSpent = true
      this.$inertia.post('/shoppings', {
        transaction_type: 'spent',
        ...this.quickSpentForm,
      }, {
        onSuccess: () => {
          this.isSavingSpent = false
          this.$emit('close')
          Swal.fire({
            icon: 'success',
            title: '¡Gasto Registrado!',
            text: 'El gasto ha sido registrado exitosamente como egreso al negocio.',
            timer: 2000,
            showConfirmButton: false,
          })
        },
        onError: (errors) => {
          this.isSavingSpent = false
          const msg = Object.values(errors)[0] || 'Error al guardar el gasto'
          Swal.fire({ icon: 'error', title: 'Error', text: msg })
        },
      })
    },
  },
}
</script>
